import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Tooltip,
  Chip,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Badge,
  TextField,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
  Collapse,
  IconButton,
  Checkbox,
  FormGroup,
  CircularProgress,
  Radio,
  RadioGroup,
  FormControl,
  Drawer,
  Divider,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import BookmarkAddIcon from '@mui/icons-material/BookmarkAdd';
import EventSeatIcon from '@mui/icons-material/EventSeat';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DateRangeIcon from '@mui/icons-material/DateRange';
import PersonIcon from '@mui/icons-material/Person';
import GroupIcon from '@mui/icons-material/Group';
import LockIcon from '@mui/icons-material/Lock';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CloseIcon from '@mui/icons-material/Close';
import {
  format,
  startOfWeek,
  addDays,
  isWeekend,
  isSameDay,
  eachDayOfInterval,
  isWithinInterval,
  getDay,
} from 'date-fns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { getDefaultLayout, getAllSeatsFromLayout, getAllZoneBlocks, CABINS } from '../config/floorLayout';
import './FloorPlan.css';

// Fixed seats that cannot be booked (hardcoded defaults)
const DEFAULT_FIXED_SEATS = [];

// Day name to getDay() number mapping
const DAY_MAP = { Monday: 1, Tuesday: 2, Wednesday: 3, Thursday: 4, Friday: 5 };
const ALL_WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

const getUserPreferredDays = (user) => {
  const days = [];
  if (user?.preferred_day1) days.push(user.preferred_day1.trim());
  if (user?.preferred_day2) days.push(user.preferred_day2.trim());
  if (user?.preferred_day3) days.push(user.preferred_day3.trim());
  return days.length > 0 ? days : ALL_WEEKDAYS;
};

// Default end date (3 months from now), overridden by admin settings
const DEFAULT_BOOKING_END = new Date();
DEFAULT_BOOKING_END.setMonth(DEFAULT_BOOKING_END.getMonth() + 3);

const todayMidnight = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

// Column-first cluster selection.
// Fills the clicked seat's column (desk pod) completely before expanding to
// adjacent columns, so physically adjacent columns (e.g. 178-181 next to 117-114)
// are always treated as a unit. Overflows to other blocks when the primary block
// is exhausted.
const findCluster = (clickedSeat, teamSize, isSeatAvailable, zoneBlocks) => {
  let primaryBlock = null;
  let clickedPos = null;

  for (const block of zoneBlocks) {
    for (let r = 0; r < block.length; r++) {
      for (let c = 0; c < block[r].length; c++) {
        if (block[r][c] === clickedSeat) {
          primaryBlock = block;
          clickedPos = { r, c };
          break;
        }
      }
      if (clickedPos) break;
    }
    if (primaryBlock) break;
  }

  if (!primaryBlock) return [];

  const numRows = primaryBlock.length;
  const numCols = primaryBlock[0].length;
  const { r: clickedRow, c: clickedCol } = clickedPos;

  const primaryBlockSeats = new Set();
  primaryBlock.forEach(row => row.forEach(s => { if (s !== null) primaryBlockSeats.add(s); }));

  // Visit columns: clicked column first, then alternate left/right
  const colOrder = [clickedCol];
  let left = clickedCol - 1;
  let right = clickedCol + 1;
  while (left >= 0 || right < numCols) {
    if (left >= 0) { colOrder.push(left); left--; }
    if (right < numCols) { colOrder.push(right); right++; }
  }

  // Within each column, start at clicked row then alternate up/down
  const cluster = [];
  for (const col of colOrder) {
    if (cluster.length >= teamSize) break;
    const rowOrder = [clickedRow];
    let up = clickedRow - 1;
    let down = clickedRow + 1;
    while (up >= 0 || down < numRows) {
      if (up >= 0) { rowOrder.push(up); up--; }
      if (down < numRows) { rowOrder.push(down); down++; }
    }
    for (const row of rowOrder) {
      if (cluster.length >= teamSize) break;
      const seat = primaryBlock[row][col];
      if (seat !== null && isSeatAvailable(seat)) cluster.push(seat);
    }
  }

  // Overflow into other blocks when the primary block has fewer seats than teamSize.
  // The physical corridor divides the floor into two sections:
  //   Upper (above corridor): zoneAUpper(0), zoneCUpper(3)
  //   Lower (below corridor): zoneALower(1), zoneB(2), zoneCLower(4)
  // Rule: exhaust same section first, then cross the corridor.
  // Within each section, prefer blocks whose index is closest to the primary.
  if (cluster.length < teamSize) {
    const clusterSet = new Set(cluster);
    const primaryIndex = zoneBlocks.indexOf(primaryBlock);
    const LOWER_SECTION = new Set([1, 2, 4]);
    const primaryIsLower = LOWER_SECTION.has(primaryIndex);

    const otherBlocks = zoneBlocks
      .map((block, idx) => {
        if (block === primaryBlock) return null;
        const isLower = LOWER_SECTION.has(idx);
        const sectionOrder = (isLower === primaryIsLower) ? 0 : 1;
        return { block, sectionOrder, blockDist: Math.abs(idx - primaryIndex) };
      })
      .filter(Boolean)
      .sort((a, b) => a.sectionOrder - b.sectionOrder || a.blockDist - b.blockDist);

    for (const { block } of otherBlocks) {
      if (cluster.length >= teamSize) break;
      const blockSeats = [];
      for (const row of block) {
        for (const s of row) {
          if (s !== null && !clusterSet.has(s) && isSeatAvailable(s)) blockSeats.push(s);
        }
      }
      blockSeats.sort((a, b) => Math.abs(a - clickedSeat) - Math.abs(b - clickedSeat));
      for (const s of blockSeats) {
        if (cluster.length >= teamSize) break;
        cluster.push(s);
        clusterSet.add(s);
      }
    }
  }

  return cluster.slice(0, teamSize);
};

// Helper to get contrasting text color
const getContrastColor = (hexColor) => {
  if (!hexColor) return '#fff';
  const r = parseInt(hexColor.slice(1, 3), 16);
  const g = parseInt(hexColor.slice(3, 5), 16);
  const b = parseInt(hexColor.slice(5, 7), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#000' : '#fff';
};

const FloorPlan = ({ bookingMode: externalMode, initialDate }) => {
  // externalMode: 'block' = block booking (no day tabs), 'individual' = individual day (no block panel)
  const isBlockMode = externalMode === 'block';
  const isIndividualMode = externalMode === 'individual';

  const { user } = useAuth();
  const [layout, setLayout] = useState(getDefaultLayout());
  const [bookingStart, setBookingStart] = useState(todayMidnight);
  const [bookingEnd, setBookingEnd] = useState(DEFAULT_BOOKING_END);
  const QUARTER_START = bookingStart;
  const QUARTER_END = bookingEnd;
  const [selectedDate, setSelectedDate] = useState(() => {
    if (initialDate) {
      const d = new Date(initialDate + 'T00:00:00');
      if (!isNaN(d.getTime())) return d;
    }
    const today = new Date();
    if (isWeekend(today)) return addDays(startOfWeek(today, { weekStartsOn: 1 }), 7);
    return today;
  });
  const [bookings, setBookings] = useState([]);
  const [selectedSeats, setSelectedSeats] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [releaseDialog, setReleaseDialog] = useState({ open: false, seatNumber: null, bookingId: null });
  const [loading, setLoading] = useState(false);
  const [bookingMode, setBookingMode] = useState('group');
  const [blockBookingOpen, setBlockBookingOpen] = useState(isBlockMode);
  const [blockStartDate, setBlockStartDate] = useState('');
  const [blockEndDate, setBlockEndDate] = useState('');
  const [blockSearched, setBlockSearched] = useState(false);
  const [fixedSeatsFromDB, setFixedSeatsFromDB] = useState(null);
  const [reserveDialog, setReserveDialog] = useState({ open: false, seatNumber: null });
  const [selectedDays, setSelectedDays] = useState(() => getUserPreferredDays(user));
  const [previewDialog, setPreviewDialog] = useState({ open: false, preview: null, dateStrings: [] });
  const [todayBookingType, setTodayBookingType] = useState('team'); // 'team' | 'additional'
  const [bookingDrawerOpen, setBookingDrawerOpen] = useState(false);
  const [autoSeats, setAutoSeats] = useState({});       // { date: { seats: [], partial: bool } }
  const [autoSeatsLoading, setAutoSeatsLoading] = useState({}); // { date: bool }
  const [holidays, setHolidays] = useState(new Set());

  const weekStart = startOfWeek(selectedDate, { weekStartsOn: 1 });
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const weekDays = Array.from({ length: 5 }, (_, i) => addDays(weekStart, i))
    .filter(d =>
      isWithinInterval(d, { start: QUARTER_START, end: QUARTER_END }) &&
      d >= today &&
      !holidays.has(format(d, 'yyyy-MM-dd'))
    );
  const currentDayIndex = weekDays.findIndex((d) => isSameDay(d, selectedDate));

  // If DB data loaded, trust it; otherwise fall back to hardcoded defaults
  const allFixedSeats = fixedSeatsFromDB !== null ? fixedSeatsFromDB : DEFAULT_FIXED_SEATS;

  // Load layout from DB
  const fetchLayout = useCallback(async () => {
    try {
      const res = await api.get('/seat-layout/grid');
      if (res.data) setLayout(res.data);
    } catch (err) {
      console.error('Failed to fetch layout:', err);
    }
  }, []);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await api.get('/settings');
      if (res.data.booking_allowed_from) {
        const fromDate = new Date(res.data.booking_allowed_from + 'T00:00:00');
        setBookingStart(fromDate);
        // If the currently selected date is before the allowed start, move it forward
        setSelectedDate(prev => (prev < fromDate ? fromDate : prev));
      }
      if (res.data.booking_allowed_till) {
        setBookingEnd(new Date(res.data.booking_allowed_till + 'T23:59:59'));
      }
    } catch (err) {
      console.error('Failed to fetch settings:', err);
    }
  }, []);

  const fetchHolidays = useCallback(async () => {
    try {
      const res = await api.get('/holidays');
      setHolidays(new Set(res.data.map(h => h.holiday_date)));
    } catch (err) {
      console.error('Failed to fetch holidays:', err);
    }
  }, []);

  useEffect(() => { fetchSettings(); fetchHolidays(); }, [fetchSettings, fetchHolidays]);

  const fetchBookings = useCallback(async () => {
    try {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const response = await api.get('/bookings', { params: { date: dateStr } });
      setBookings(response.data);
    } catch (err) {
      console.error('Failed to fetch bookings:', err);
      setBookings([]);
    }
  }, [selectedDate]);

  const fetchFixedSeats = useCallback(async () => {
    try {
      const response = await api.get('/seats');
      const fixed = response.data.filter(s => s.is_fixed).map(s => s.seat_number);
      setFixedSeatsFromDB(fixed);
    } catch (err) {
      console.error('Failed to fetch seats:', err);
      setFixedSeatsFromDB(DEFAULT_FIXED_SEATS);
    }
  }, []);

  useEffect(() => { fetchLayout(); }, [fetchLayout]);

  useEffect(() => {
    fetchBookings();
    setSelectedSeats([]);
  }, [fetchBookings]);

  useEffect(() => { fetchFixedSeats(); }, [fetchFixedSeats]);

  const getSeatStatus = (seatNumber) => {
    if (allFixedSeats.includes(seatNumber)) return 'fixed';
    if (selectedSeats.includes(seatNumber)) return 'selected';
    const booking = bookings.find(
      (b) => b.seat_number === seatNumber || b.seatNumber === seatNumber
    );
    if (booking) {
      const bookingUserId = booking.user_id || booking.userId;
      if (bookingUserId === user?.id) return 'mine';
      return 'booked';
    }
    return 'available';
  };

  const getBookingInfo = (seatNumber) => {
    return bookings.find(
      (b) => b.seat_number === seatNumber || b.seatNumber === seatNumber
    );
  };

  // Dynamic seat list and zone blocks from layout
  const ALL_SEATS = getAllSeatsFromLayout(layout);
  const ALL_ZONE_BLOCKS = getAllZoneBlocks(layout);

  // Stats
  const totalSeats = ALL_SEATS.length;
  const fixedCount = allFixedSeats.length;
  const bookedCount = bookings.filter((b) => {
    const uid = b.user_id || b.userId;
    return uid !== user?.id;
  }).length;
  const myBookedCount = bookings.filter((b) => {
    const uid = b.user_id || b.userId;
    return uid === user?.id;
  }).length;
  const availableCount = totalSeats - fixedCount - bookings.length;

  const teamSize = user?.team_size || 1;

  // On today's date (not a holiday, within booking window, not block mode) users may book beyond their team quota
  const selectedDateStr = format(selectedDate, 'yyyy-MM-dd');
  const todayStr = format(new Date(), 'yyyy-MM-dd');
  const isUnlimitedDay =
    !blockBookingOpen &&
    selectedDateStr === todayStr &&
    !holidays.has(selectedDateStr) &&
    isWithinInterval(selectedDate, { start: QUARTER_START, end: QUARTER_END });

  const isAdditionalMode = isUnlimitedDay && todayBookingType === 'additional';
  const remainingQuota = isAdditionalMode
    ? availableCount                               // all remaining seats on the floor
    : Math.max(0, teamSize - myBookedCount);

  const isSeatAvailableForCluster = useCallback((seatNum) => {
    if (allFixedSeats.includes(seatNum)) return false;
    const booked = bookings.find(
      (b) => b.seat_number === seatNum || b.seatNumber === seatNum
    );
    return !booked;
  }, [bookings, allFixedSeats]);

  const handleSeatClick = (seatNumber) => {
    const status = getSeatStatus(seatNumber);
    if (status === 'fixed') return;

    if (status === 'mine') {
      const booking = getBookingInfo(seatNumber);
      if (booking) {
        setReleaseDialog({
          open: true,
          seatNumber,
          bookingId: booking._id || booking.id,
        });
      }
      return;
    }

    if (status === 'booked') return;

    if (status === 'selected') {
      if (bookingMode === 'individual') {
        setSelectedSeats((prev) => prev.filter((s) => s !== seatNumber));
      } else {
        setSelectedSeats([]);
      }
      return;
    }

    if (isAdditionalMode) {
      // Additional Seat Reservation — one by one, no quota limit
      if (selectedSeats.length >= remainingQuota) {
        setSnackbar({ open: true, message: 'No available seats left to select.', severity: 'warning' });
        return;
      }
      setSelectedSeats((prev) => [...prev, seatNumber]);
    } else if (bookingMode === 'individual') {
      // Normal individual mode: toggle seats up to quota
      if (selectedSeats.length >= remainingQuota) {
        setSnackbar({
          open: true,
          message: `You can select up to ${remainingQuota} more seat(s). Deselect one first or book current selection.`,
          severity: 'warning',
        });
        return;
      }
      setSelectedSeats((prev) => [...prev, seatNumber]);
    } else {
      // Group mode: auto-select cluster
      if (remainingQuota <= 0) {
        setSnackbar({
          open: true,
          message: `You have already booked ${myBookedCount} seats (team size: ${teamSize}).`,
          severity: 'warning',
        });
        return;
      }
      const cluster = findCluster(seatNumber, teamSize, isSeatAvailableForCluster, ALL_ZONE_BLOCKS);
      if (cluster.length > 0) {
        setSelectedSeats(cluster);
        setSnackbar({
          open: true,
          message: `Auto-selected ${cluster.length} adjacent seat(s). Click another seat to re-select.`,
          severity: 'info',
        });
      }
    }
  };

  const handleSeatRightClick = (e, seatNumber) => {
    e.preventDefault();
    if (user?.role === 'admin') {
      setReserveDialog({ open: true, seatNumber });
    }
  };

  const handleReserveSeat = async () => {
    const { seatNumber } = reserveDialog;
    const isCurrentlyFixed = allFixedSeats.includes(seatNumber);
    try {
      await api.put('/seat-layout/reserve', {
        seat_numbers: [seatNumber],
        is_fixed: !isCurrentlyFixed,
        fixed_label: !isCurrentlyFixed ? 'Admin Reserved' : null,
      });
      setSnackbar({
        open: true,
        message: isCurrentlyFixed ? `Seat ${seatNumber} unreserved` : `Seat ${seatNumber} reserved`,
        severity: 'success',
      });
      setReserveDialog({ open: false, seatNumber: null });
      fetchFixedSeats();
      fetchBookings();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Failed to update seat',
        severity: 'error',
      });
    }
  };

  const getBlockDateStrings = () => {
    const start = new Date(blockStartDate + 'T00:00:00');
    const end = new Date(blockEndDate + 'T00:00:00');
    const selectedDayNums = selectedDays.map(d => DAY_MAP[d]);
    return eachDayOfInterval({ start, end })
      .filter(d =>
        !isWeekend(d) &&
        isWithinInterval(d, { start: QUARTER_START, end: QUARTER_END }) &&
        selectedDayNums.includes(getDay(d)) &&
        d >= today &&
        !holidays.has(format(d, 'yyyy-MM-dd'))
      )
      .map(d => format(d, 'yyyy-MM-dd'));
  };

  const handleBook = async () => {
    if (selectedSeats.length === 0) return;
    setLoading(true);

    try {
      if (blockBookingOpen && blockStartDate && blockEndDate) {
        const dateStrings = getBlockDateStrings();
        if (dateStrings.length === 0) {
          setSnackbar({ open: true, message: 'No valid weekdays in selected range.', severity: 'warning' });
          setLoading(false);
          return;
        }

        // Preview first - check for conflicts
        const preview = await api.post('/bookings/multi-day/preview', {
          seats: selectedSeats,
          dates: dateStrings,
        });

        if (preview.data.conflictDays > 0) {
          // Show preview dialog with conflicts
          setPreviewDialog({ open: true, preview: preview.data, dateStrings });
          setLoading(false);
          return;
        }

        // No conflicts - book directly
        const response = await api.post('/bookings/multi-day', {
          seats: selectedSeats,
          dates: dateStrings,
        });
        setSnackbar({
          open: true,
          message: response.data.message || `Booked for ${dateStrings.length} day(s)!`,
          severity: 'success',
        });
      } else {
        const dateStr = format(selectedDate, 'yyyy-MM-dd');
        await api.post('/bookings', { seats: selectedSeats, date: dateStr });
        setSnackbar({
          open: true,
          message: `Successfully booked ${selectedSeats.length} seat(s)!`,
          severity: 'success',
        });
      }
      setSelectedSeats([]);
      fetchBookings();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Booking failed.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  // Find auto-alternative seats for a conflict day
  const handleFindAutoSeats = async (date) => {
    setAutoSeatsLoading(prev => ({ ...prev, [date]: true }));
    try {
      const resp = await api.get('/bookings', { params: { date } });
      const bookedNums = new Set(resp.data.map(b => b.seat_number || b.seatNumber));
      const isSeatAvailableOnDay = (seatNum) =>
        !allFixedSeats.includes(seatNum) && !bookedNums.has(seatNum);

      const needed = selectedSeats.length;
      // Try each original seat as cluster center; pick the result with the most seats
      let best = [];
      for (const startSeat of selectedSeats) {
        const cluster = findCluster(startSeat, needed, isSeatAvailableOnDay, ALL_ZONE_BLOCKS);
        if (cluster.length > best.length) best = cluster;
        if (best.length >= needed) break;
      }

      if (best.length === 0) {
        setAutoSeats(prev => ({ ...prev, [date]: { seats: [], partial: false, noSeats: true } }));
      } else {
        setAutoSeats(prev => ({ ...prev, [date]: { seats: best, partial: best.length < needed, noSeats: false } }));
      }
    } catch (err) {
      console.error('Auto seat find failed:', err);
    } finally {
      setAutoSeatsLoading(prev => ({ ...prev, [date]: false }));
    }
  };

  const closePreviewDialog = () => {
    setPreviewDialog({ open: false, preview: null, dateStrings: [] });
    setAutoSeats({});
    setAutoSeatsLoading({});
  };

  // Confirm block booking — clear days with original seats, conflict days with auto seats
  const handleConfirmBlockBooking = async () => {
    const { preview } = previewDialog;
    if (!preview) return;
    setLoading(true);

    try {
      const clearDates = preview.days.filter(d => d.status === 'clear').map(d => d.date);
      const conflictDatesWithAuto = preview.days.filter(
        d => d.status === 'conflict' && autoSeats[d.date]?.seats?.length > 0
      );
      const skippedCount = preview.days.filter(
        d => d.status === 'conflict' && !autoSeats[d.date]?.seats?.length
      ).length;

      if (clearDates.length === 0 && conflictDatesWithAuto.length === 0) {
        setSnackbar({ open: true, message: 'Nothing to book.', severity: 'warning' });
        closePreviewDialog();
        setLoading(false);
        return;
      }

      const messages = [];

      // Book clear days with original seats
      if (clearDates.length > 0) {
        await api.post('/bookings/multi-day', { seats: selectedSeats, dates: clearDates });
        messages.push(`${clearDates.length} available day(s) booked`);
      }

      // Book each conflict day with its auto seats
      for (const day of conflictDatesWithAuto) {
        await api.post('/bookings', { seats: autoSeats[day.date].seats, date: day.date });
        messages.push(`${format(new Date(day.date + 'T00:00:00'), 'dd MMM')} booked with alternative seats`);
      }

      if (skippedCount > 0) messages.push(`${skippedCount} day(s) skipped`);

      setSnackbar({ open: true, message: messages.join('. ') + '.', severity: 'success' });
      setSelectedSeats([]);
      closePreviewDialog();
      fetchBookings();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Booking failed.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRelease = async () => {
    const { bookingId } = releaseDialog;
    if (!bookingId) return;
    try {
      await api.delete(`/bookings/${bookingId}`);
      setSnackbar({ open: true, message: 'Seat released successfully!', severity: 'success' });
      setReleaseDialog({ open: false, seatNumber: null, bookingId: null });
      fetchBookings();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Failed to release seat.',
        severity: 'error',
      });
    }
  };

  const snapToWeekday = (dateStr) => {
    let d = new Date(dateStr + 'T00:00:00');
    if (isWeekend(d)) {
      // Advance to next Monday
      do { d = addDays(d, 1); } while (isWeekend(d));
    }
    return d;
  };

  const handleDateChange = (e) => {
    const val = e.target.value;
    let newDate = new Date(val + 'T00:00:00');
    if (isWeekend(newDate)) {
      newDate = snapToWeekday(val);
      setSnackbar({ open: true, message: 'Weekends are not bookable. Moved to next Monday.', severity: 'info' });
    }
    if (!isWithinInterval(newDate, { start: QUARTER_START, end: QUARTER_END })) return;
    if (newDate < today) {
      setSnackbar({ open: true, message: 'Cannot select a past date.', severity: 'warning' });
      return;
    }
    if (holidays.has(format(newDate, 'yyyy-MM-dd'))) {
      setSnackbar({ open: true, message: 'This date is a holiday. Booking is not allowed.', severity: 'warning' });
      return;
    }
    setSelectedDate(newDate);
  };

  const handleDayTab = (_, newValue) => {
    if (weekDays[newValue]) setSelectedDate(weekDays[newValue]);
  };

  // Get unique leaders who have bookings today
  const leaderColors = {};
  bookings.forEach(b => {
    const name = b.user_name || b.userName || 'Unknown';
    const abbr = b.user_abbreviation || b.userAbbreviation || '?';
    const color = b.user_color || b.userColor || '#f44336';
    const uid = b.user_id || b.userId;
    if (!leaderColors[uid]) {
      leaderColors[uid] = { name, abbr, color, count: 0 };
    }
    leaderColors[uid].count++;
  });

  const renderSeat = (seatNumber) => {
    if (seatNumber === null) return <div className="seat-empty" key={`empty-${Math.random()}`} />;

    const status = getSeatStatus(seatNumber);
    const booking = getBookingInfo(seatNumber);
    const leaderColor = booking?.user_color || booking?.userColor;
    const leaderAbbr = booking?.user_abbreviation || booking?.userAbbreviation;

    // Dynamic style for booked seats with leader color
    let seatStyle = {};
    let seatClass = 'seat';

    if (status === 'booked' && leaderColor) {
      seatStyle = {
        backgroundColor: leaderColor,
        color: getContrastColor(leaderColor),
        borderColor: leaderColor,
        cursor: 'not-allowed',
      };
    } else if (status === 'mine') {
      const myColor = user?.color || '#ffc107';
      seatStyle = {
        backgroundColor: myColor,
        color: getContrastColor(myColor),
        borderColor: myColor,
      };
      seatClass += ' seat-mine';
    } else if (status === 'fixed') {
      seatClass += ' seat-fixed';
    } else if (status === 'selected') {
      seatClass += ' seat-selected';
    } else {
      seatClass += ' seat-available';
    }

    const tooltipTitle =
      status === 'booked'
        ? `Seat ${seatNumber} - ${booking?.user_name || 'Unknown'} (${leaderAbbr || ''})`
        : status === 'mine'
        ? `Seat ${seatNumber} - Your booking (click to release)`
        : status === 'fixed'
        ? `Seat ${seatNumber} - Fixed/Reserved`
        : `Seat ${seatNumber} - Available`;

    return (
      <Tooltip title={tooltipTitle} arrow key={seatNumber}>
        <div
          className={seatClass}
          style={seatStyle}
          onClick={() => handleSeatClick(seatNumber)}
          onContextMenu={(e) => handleSeatRightClick(e, seatNumber)}
        >
          {(status === 'booked' || status === 'mine') && leaderAbbr
            ? leaderAbbr
            : seatNumber}
        </div>
      </Tooltip>
    );
  };

  const renderSeatRow = (seats, keyPrefix) => (
    <div className="seat-row" key={keyPrefix}>
      {seats.map((seat) => renderSeat(seat))}
    </div>
  );

  const renderZone = (rows, label, keyPrefix) => (
    <div className="zone-block" key={keyPrefix}>
      {rows.map((row, idx) => renderSeatRow(row, `${keyPrefix}-row-${idx}`))}
    </div>
  );

  return (
    <Box>
      {/* Header with date controls */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 2 }}>
          <Typography variant="h5" fontWeight={600} sx={{ color: '#052e2b' }}>
            7th Floor - Seat Map
          </Typography>
          <Chip
            label={`Allocated: ${teamSize} seat(s)`}
            size="small"
            sx={{ backgroundColor: '#052e2b', color: '#68DA6A', fontWeight: 600 }}
          />
          <Box sx={{ flexGrow: 1 }} />
          {!isUnlimitedDay && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonIcon fontSize="small" color={bookingMode === 'individual' ? 'primary' : 'disabled'} />
              <FormControlLabel
                control={
                  <Switch
                    checked={bookingMode === 'group'}
                    onChange={(e) => {
                      setBookingMode(e.target.checked ? 'group' : 'individual');
                      setSelectedSeats([]);
                    }}
                    color="primary"
                    size="small"
                  />
                }
                label=""
                sx={{ mx: 0 }}
              />
              <GroupIcon fontSize="small" color={bookingMode === 'group' ? 'primary' : 'disabled'} />
              <Typography variant="caption" sx={{ ml: 0.5 }}>
                {bookingMode === 'group' ? 'Group Booking' : 'Individual'}
              </Typography>
            </Box>
          )}
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              value={selectedDate}
              onChange={(newDate) => {
                if (!newDate) return;
                handleDateChange({ target: { value: format(newDate, 'yyyy-MM-dd') } });
              }}
              shouldDisableDate={(d) => isWeekend(d)}
              minDate={today > QUARTER_START ? today : QUARTER_START}
              maxDate={QUARTER_END}
              slotProps={{
                textField: { size: 'small', sx: { width: 180 } },
                day: {
                  sx: {
                    '&.Mui-disabled': { color: '#ccc !important' },
                  },
                },
              }}
            />
          </LocalizationProvider>
        </Box>

        {/* Day tabs - hidden in block mode */}
        {!isBlockMode && (
          <Tabs
            value={currentDayIndex >= 0 ? currentDayIndex : false}
            onChange={handleDayTab}
            variant="fullWidth"
            sx={{ '& .MuiTab-root': { minWidth: 0 } }}
          >
            {weekDays.map((day, idx) => (
              <Tab
                key={idx}
                label={
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" display="block">{format(day, 'EEE')}</Typography>
                    <Typography variant="body2" fontWeight={600}>{format(day, 'dd MMM')}</Typography>
                  </Box>
                }
              />
            ))}
          </Tabs>
        )}

        {/* Booking type toggle — only shown for today's date */}
        {isUnlimitedDay && (
          <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid #e0e0e0', display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="caption" fontWeight={600} sx={{ color: '#052e2b', whiteSpace: 'nowrap' }}>
              Booking Type:
            </Typography>
            <FormControl>
              <RadioGroup
                row
                value={todayBookingType}
                onChange={(e) => { setTodayBookingType(e.target.value); setSelectedSeats([]); }}
              >
                <FormControlLabel
                  value="team"
                  control={<Radio size="small" sx={{ color: '#052e2b', '&.Mui-checked': { color: '#052e2b' } }} />}
                  label={<Typography variant="body2">Team Booking</Typography>}
                />
                <FormControlLabel
                  value="additional"
                  control={<Radio size="small" sx={{ color: '#052e2b', '&.Mui-checked': { color: '#052e2b' } }} />}
                  label={<Typography variant="body2">Additional Seat Reservation <Typography component="span" variant="caption" color="text.secondary">(today only)</Typography></Typography>}
                />
              </RadioGroup>
            </FormControl>
          </Box>
        )}
      </Paper>

      {/* Block Booking Panel - hidden in individual mode */}
      {!isIndividualMode && (
        <Paper sx={{ mb: 2 }}>
          <Box
            sx={{ p: 1.5, display: 'flex', alignItems: 'center', cursor: isBlockMode ? 'default' : 'pointer', gap: 1 }}
            onClick={() => { if (!isBlockMode) setBlockBookingOpen(!blockBookingOpen); }}
          >
            <DateRangeIcon sx={{ color: '#052e2b' }} />
            <Typography variant="subtitle2" fontWeight={600} sx={{ flexGrow: 1 }}>
              {isBlockMode ? 'Select Date Range' : 'Block Booking (Multiple Days)'}
            </Typography>
            {!isBlockMode && (
              <IconButton size="small">
                {blockBookingOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            )}
          </Box>
          <Collapse in={blockBookingOpen}>
            <Box sx={{ px: 2, pb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Start Date"
                  value={blockStartDate ? new Date(blockStartDate + 'T00:00:00') : null}
                  onChange={(d) => {
                    if (!d) return;
                    setBlockStartDate(format(d, 'yyyy-MM-dd'));
                    setBlockSearched(false);
                  }}
                  shouldDisableDate={(d) => isWeekend(d)}
                  minDate={today > QUARTER_START ? today : QUARTER_START}
                  maxDate={QUARTER_END}
                  slotProps={{ textField: { size: 'small', sx: { width: 170 } } }}
                />
                <DatePicker
                  label="End Date"
                  value={blockEndDate ? new Date(blockEndDate + 'T00:00:00') : null}
                  onChange={(d) => {
                    if (!d) return;
                    setBlockEndDate(format(d, 'yyyy-MM-dd'));
                    setBlockSearched(false);
                  }}
                  shouldDisableDate={(d) => isWeekend(d)}
                  minDate={blockStartDate ? new Date(blockStartDate + 'T00:00:00') : (today > QUARTER_START ? today : QUARTER_START)}
                  maxDate={QUARTER_END}
                  slotProps={{ textField: { size: 'small', sx: { width: 170 } } }}
                />
              </LocalizationProvider>
              {isBlockMode && (
                <Button
                  variant="contained"
                  onClick={() => {
                    if (blockStartDate && blockEndDate) {
                      setBlockSearched(true);
                      setSelectedDate(new Date(blockStartDate + 'T00:00:00'));
                    }
                  }}
                  disabled={!blockStartDate || !blockEndDate}
                  sx={{ backgroundColor: '#052e2b', '&:hover': { backgroundColor: '#0a4a45' } }}
                >
                  Search
                </Button>
              )}
              {/* Day-of-week selection — max 3 */}
              <Box sx={{ width: '100%', mt: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Typography variant="caption" fontWeight={600} sx={{ color: '#052e2b' }}>
                    Select Days of Week:
                  </Typography>
                  <Typography variant="caption" color={selectedDays.length >= 3 ? 'error' : 'text.secondary'}>
                    ({selectedDays.length}/3 selected)
                  </Typography>
                </Box>
                <FormGroup row sx={{ gap: 0 }}>
                  {ALL_WEEKDAYS.map((day) => {
                    const isChecked = selectedDays.includes(day);
                    const isDisabled = !isChecked && selectedDays.length >= 3;
                    return (
                      <FormControlLabel
                        key={day}
                        control={
                          <Checkbox
                            size="small"
                            checked={isChecked}
                            disabled={isDisabled}
                            onChange={(e) => {
                              if (e.target.checked) {
                                if (selectedDays.length >= 3) return;
                                setSelectedDays(prev => [...prev, day]);
                              } else {
                                setSelectedDays(prev => prev.filter(d => d !== day));
                              }
                              setBlockSearched(false);
                            }}
                            sx={{ '&.Mui-checked': { color: '#052e2b' } }}
                          />
                        }
                        label={<Typography variant="caption" color={isDisabled ? 'text.disabled' : 'inherit'}>{day}</Typography>}
                        sx={{ mr: 1.5 }}
                      />
                    );
                  })}
                </FormGroup>
              </Box>
              {blockStartDate && blockEndDate && selectedDays.length > 0 && (
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {(() => {
                    try {
                      const selectedDayNums = selectedDays.map(d => DAY_MAP[d]);
                      const days = eachDayOfInterval({
                        start: new Date(blockStartDate + 'T00:00:00'),
                        end: new Date(blockEndDate + 'T00:00:00'),
                      }).filter(d =>
                        !isWeekend(d) &&
                        isWithinInterval(d, { start: QUARTER_START, end: QUARTER_END }) &&
                        selectedDayNums.includes(getDay(d)) &&
                        d >= today &&
                        !holidays.has(format(d, 'yyyy-MM-dd'))
                      );
                      return (
                        <>
                          <Chip label={`${days.length} day(s) matched`} size="small" color="primary" />
                          {days.slice(0, 5).map(d => (
                            <Chip key={d.toISOString()} label={format(d, 'dd MMM (EEE)')} size="small" variant="outlined" />
                          ))}
                          {days.length > 5 && <Chip label={`+${days.length - 5} more`} size="small" variant="outlined" />}
                        </>
                      );
                    } catch { return null; }
                  })()}
                </Box>
              )}
              <Typography variant="caption" color="text.secondary">
                {isBlockMode ? 'Select date range, days, and click Search to view floor plan.' : 'Max 11 working days. Select seats below then click Book.'}
              </Typography>
            </Box>
          </Collapse>
        </Paper>
      )}

      {/* Floating book button */}
      {selectedSeats.length > 0 && !bookingDrawerOpen && (
        <Box sx={{ position: 'fixed', bottom: 32, right: 32, zIndex: 1300 }}>
          <Button
            variant="contained"
            size="large"
            startIcon={<Badge badgeContent={selectedSeats.length} color="error"><BookmarkAddIcon /></Badge>}
            onClick={() => setBookingDrawerOpen(true)}
            sx={{
              backgroundColor: '#052e2b',
              color: 'white',
              px: 3,
              py: 1.5,
              borderRadius: 3,
              boxShadow: '0 4px 20px rgba(5,46,43,0.4)',
              '&:hover': { backgroundColor: '#0a4a45' },
            }}
          >
            Book
          </Button>
        </Box>
      )}

      {/* Stats, Floor Plan, and actions - in block mode only shown after Search */}
      {(!isBlockMode || blockSearched) && <>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <Chip label={`Total: ${totalSeats}`} size="small" variant="outlined" />
            <Chip label={`Available: ${availableCount}`} size="small" sx={{ backgroundColor: '#68DA6A', color: '#052e2b' }} />
            <Chip label={`Booked: ${bookedCount}`} size="small" sx={{ backgroundColor: '#f44336', color: 'white' }} />
            <Chip label={`My Bookings: ${myBookedCount}`} size="small" sx={{ backgroundColor: user?.color || '#ffc107', color: getContrastColor(user?.color || '#ffc107') }} />
            <Chip label={`Fixed: ${fixedCount}`} size="small" sx={{ backgroundColor: '#9e9e9e', color: 'white' }} />
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <div className="legend-box legend-available" />
              <Typography variant="caption">Available</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <div className="legend-box legend-selected" />
              <Typography variant="caption">Selected</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <div className="legend-box legend-fixed" />
              <Typography variant="caption">Fixed</Typography>
            </Box>
          </Box>
        </Box>
        {/* Leader color legend */}
        {Object.keys(leaderColors).length > 0 && (
          <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid #e0e0e0' }}>
            <Typography variant="caption" fontWeight={600} sx={{ mb: 0.5, display: 'block' }}>
              Team Leaders on this day:
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {Object.values(leaderColors).map((leader) => (
                <Chip
                  key={leader.abbr}
                  label={`${leader.abbr} - ${leader.name} (${leader.count})`}
                  size="small"
                  sx={{
                    backgroundColor: leader.color,
                    color: getContrastColor(leader.color),
                    fontWeight: 600,
                    fontSize: '0.7rem',
                  }}
                />
              ))}
            </Box>
          </Box>
        )}
      </Paper>

      {/* Floor Plan - same layout structure as before */}
      <Paper sx={{ p: 3, overflowX: 'auto' }}>
        <div className="floor-plan-wrapper">
          {/* Main area (zones + rooms + corridor) */}
          <div className="floor-plan-main">
            {/* Upper section */}
            <div className="floor-upper">
              <div className="zone-container">
                <Typography variant="subtitle2" className="zone-label" sx={{ mb: 1, fontWeight: 700, color: '#052e2b' }}>
                  Zone C (114-181)
                </Typography>
                {renderZone(layout.zoneAUpper, 'Zone A Upper', 'zone-a-upper')}
              </div>

              <div className="rooms-middle">
                <div className="rooms-strip">
                  <div className="room-block server-room">
                    <Typography variant="caption" fontWeight={600}>Server Room</Typography>
                  </div>
                  <div className="room-block meeting-room">
                    <Typography variant="caption" fontWeight={600}>Ravi</Typography>
                  </div>
                  <div className="room-block meeting-room">
                    <Typography variant="caption" fontWeight={600}>Sutlej</Typography>
                  </div>
                  <div className="room-block meeting-room">
                    <Typography variant="caption" fontWeight={600}>Saraswati</Typography>
                  </div>
                  <div className="room-block entry-block">
                    <Typography variant="caption" fontWeight={700}>Entry to<br/>the floor</Typography>
                  </div>
                </div>
              </div>

              <div className="zone-container">
                <Typography variant="subtitle2" className="zone-label" sx={{ mb: 1, fontWeight: 700, color: '#052e2b' }}>
                  Zone A (1-74)
                </Typography>
                <div className="zone-c-upper-spacer" />
                {renderZone(layout.zoneCUpper, 'Zone C Upper', 'zone-c-upper')}
              </div>
            </div>

            {/* Corridor */}
            <div className="corridor">
              <Typography variant="caption" sx={{ color: '#999', letterSpacing: 4, textTransform: 'uppercase' }}>
                Corridor
              </Typography>
            </div>

            {/* Lower section */}
            <div className="floor-lower">
              <div className="zone-container">
                {renderZone(layout.zoneALower, 'Zone A Lower', 'zone-a-lower')}
              </div>
              <div className="zone-container zone-b-container">
                {renderZone(layout.zoneB, 'Zone B', 'zone-b')}
                <Typography variant="subtitle2" className="zone-label" sx={{ mt: 1, fontWeight: 700, color: '#052e2b' }}>
                  Zone B (75-113)
                </Typography>
              </div>
              <div className="zone-container">
                {renderZone(layout.zoneCLower, 'Zone C Lower', 'zone-c-lower')}
              </div>
            </div>
          </div>

          {/* Cabins column - continuous on the right */}
          <div className="cabins-column">
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700, color: '#052e2b' }}>
              Cabins (Fixed)
            </Typography>
            {CABINS.map((cabin) => (
              <div className="cabin-block" key={cabin.name}>
                <Typography variant="caption" fontWeight={600} sx={{ fontSize: '0.65rem' }}>
                  {cabin.name}
                </Typography>
              </div>
            ))}
          </div>
        </div>
      </Paper>


      </>}

      {/* Booking confirmation drawer */}
      <Drawer
        anchor="right"
        open={bookingDrawerOpen}
        onClose={() => setBookingDrawerOpen(false)}
        PaperProps={{ sx: { width: 340 } }}
      >
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', backgroundColor: '#052e2b', color: 'white' }}>
          <Typography variant="h6" fontWeight={600}>Confirm Booking</Typography>
          <IconButton size="small" onClick={() => setBookingDrawerOpen(false)} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ p: 2, flexGrow: 1, overflowY: 'auto' }}>
          {/* Date / range */}
          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Date
          </Typography>
          <Typography variant="body1" fontWeight={600} sx={{ mb: 2 }}>
            {blockBookingOpen && blockStartDate && blockEndDate
              ? `${blockStartDate.split('-').reverse().join('/')} → ${blockEndDate.split('-').reverse().join('/')}`
              : format(selectedDate, 'EEEE, dd MMM yyyy')}
          </Typography>

          {/* Booking type */}
          {isAdditionalMode && (
            <Chip label="Additional Seat Reservation" size="small" color="warning" sx={{ mb: 2 }} />
          )}
          {blockBookingOpen && blockStartDate && blockEndDate && (
            <Chip
              label={`Block: ${getBlockDateStrings().length} working day(s)`}
              size="small"
              sx={{ mb: 2, backgroundColor: '#052e2b', color: 'white' }}
            />
          )}

          <Divider sx={{ mb: 2 }} />

          {/* Seat list */}
          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Selected Seats ({selectedSeats.length})
          </Typography>
          <List dense disablePadding sx={{ mt: 0.5 }}>
            {[...selectedSeats].sort((a, b) => a - b).map(seat => {
              const zone = seat >= 1 && seat <= 74 ? 'Zone A' : seat >= 75 && seat <= 113 ? 'Zone B' : 'Zone C';
              return (
                <ListItem key={seat} disableGutters sx={{ py: 0.25 }}>
                  <EventSeatIcon sx={{ fontSize: 16, color: '#052e2b', mr: 1 }} />
                  <ListItemText
                    primary={`Seat ${seat}`}
                    secondary={zone}
                    primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                </ListItem>
              );
            })}
          </List>
        </Box>

        <Divider />
        <Box sx={{ p: 2, display: 'flex', gap: 1 }}>
          <Button
            fullWidth
            variant="contained"
            startIcon={<BookmarkAddIcon />}
            onClick={() => { setBookingDrawerOpen(false); handleBook(); }}
            disabled={loading}
            sx={{ backgroundColor: '#052e2b', '&:hover': { backgroundColor: '#0a4a45' } }}
          >
            {loading ? 'Booking...' : 'Confirm'}
          </Button>
          <Button
            variant="outlined"
            onClick={() => { setBookingDrawerOpen(false); setSelectedSeats([]); }}
            sx={{ color: '#d32f2f', borderColor: '#d32f2f', whiteSpace: 'nowrap' }}
          >
            Clear
          </Button>
        </Box>
      </Drawer>

      {/* Release dialog */}
      <Dialog
        open={releaseDialog.open}
        onClose={() => setReleaseDialog({ open: false, seatNumber: null, bookingId: null })}
      >
        <DialogTitle>Release Seat</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Release Seat {releaseDialog.seatNumber}?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReleaseDialog({ open: false, seatNumber: null, bookingId: null })}>Cancel</Button>
          <Button onClick={handleRelease} color="error" variant="contained">Release</Button>
        </DialogActions>
      </Dialog>

      {/* Admin Reserve dialog */}
      {user?.role === 'admin' && (
        <Dialog
          open={reserveDialog.open}
          onClose={() => setReserveDialog({ open: false, seatNumber: null })}
        >
          <DialogTitle>
            <LockIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            {allFixedSeats.includes(reserveDialog.seatNumber) ? 'Unreserve' : 'Reserve'} Seat
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              {allFixedSeats.includes(reserveDialog.seatNumber)
                ? `Seat ${reserveDialog.seatNumber} is currently reserved. Unreserve it to make it bookable?`
                : `Reserve Seat ${reserveDialog.seatNumber}? It will become fixed and unbookable.`}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setReserveDialog({ open: false, seatNumber: null })}>Cancel</Button>
            <Button onClick={handleReserveSeat} variant="contained" sx={{ backgroundColor: '#052e2b' }}>
              {allFixedSeats.includes(reserveDialog.seatNumber) ? 'Unreserve' : 'Reserve'}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Block Booking Preview Dialog */}
      <Dialog
        open={previewDialog.open}
        onClose={closePreviewDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ color: '#052e2b', fontWeight: 600 }}>
          Block Booking Preview
        </DialogTitle>
        <DialogContent>
          {previewDialog.preview && (
            <Box>
              <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                <Chip label={`${previewDialog.preview.clearDays} day(s) available`} color="success" size="small" />
                <Chip label={`${previewDialog.preview.conflictDays} day(s) with conflicts`} color="error" size="small" />
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Conflict days have seats already taken. Use <strong>Auto Seats</strong> to find nearby alternatives — they'll be booked alongside the clear days.
              </Typography>

              <Box sx={{ maxHeight: 480, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 1 }}>
                {previewDialog.preview.days.map((day) => {
                  const auto = autoSeats[day.date];
                  const autoLoading = autoSeatsLoading[day.date];
                  return (
                    <Paper
                      key={day.date}
                      sx={{
                        p: 1.5,
                        backgroundColor: day.status === 'clear'
                          ? '#e8f5e9'
                          : auto?.seats?.length > 0
                            ? '#fff8e1'
                            : '#ffebee',
                        border: `1px solid ${day.status === 'clear' ? '#a5d6a7' : auto?.seats?.length > 0 ? '#ffe082' : '#ef9a9a'}`,
                      }}
                    >
                      {/* Day header row */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="body2" fontWeight={600} sx={{ minWidth: 160 }}>
                          {format(new Date(day.date + 'T00:00:00'), 'EEE, dd MMM yyyy')}
                        </Typography>
                        {day.status === 'clear' ? (
                          <Chip label="Available" size="small" color="success" />
                        ) : (
                          <>
                            <Chip label="Conflict" size="small" color="error" />
                            <Typography variant="caption" color="text.secondary">
                              {day.conflicts.map(c => `Seat ${c.seat_number} → ${c.abbreviation}`).join(', ')}
                            </Typography>
                          </>
                        )}
                      </Box>

                      {/* Auto seat area for conflict days */}
                      {day.status === 'conflict' && (
                        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          {!auto && !autoLoading && (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<AutoFixHighIcon />}
                              onClick={() => handleFindAutoSeats(day.date)}
                              sx={{ color: '#052e2b', borderColor: '#052e2b', fontSize: '0.75rem' }}
                            >
                              Auto Seats
                            </Button>
                          )}
                          {autoLoading && <CircularProgress size={18} />}
                          {auto && !autoLoading && (
                            <>
                              {auto.noSeats ? (
                                <Chip label="No seats available" size="small" color="error" variant="outlined" />
                              ) : (
                                <>
                                  {auto.partial && (
                                    <Chip
                                      label={`Only ${auto.seats.length} of ${selectedSeats.length} seats available nearby`}
                                      size="small"
                                      color="warning"
                                    />
                                  )}
                                  {auto.seats.map(s => (
                                    <Chip
                                      key={s}
                                      icon={<EventSeatIcon style={{ fontSize: 12 }} />}
                                      label={`Seat ${s}`}
                                      size="small"
                                      sx={{ backgroundColor: '#052e2b', color: 'white', '& .MuiChip-icon': { color: 'white' } }}
                                    />
                                  ))}
                                  <Button
                                    size="small"
                                    onClick={() => setAutoSeats(prev => { const n = { ...prev }; delete n[day.date]; return n; })}
                                    sx={{ fontSize: '0.7rem', color: 'text.secondary', minWidth: 0 }}
                                  >
                                    Clear
                                  </Button>
                                </>
                              )}
                            </>
                          )}
                        </Box>
                      )}
                    </Paper>
                  );
                })}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={closePreviewDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleConfirmBlockBooking}
            disabled={loading || (
              (previewDialog.preview?.clearDays || 0) === 0 &&
              Object.values(autoSeats).every(a => !a?.seats?.length)
            )}
            sx={{ backgroundColor: '#052e2b', '&:hover': { backgroundColor: '#0a4a45' } }}
          >
            {loading ? 'Booking...' : (() => {
              const clearCount = previewDialog.preview?.clearDays || 0;
              const autoCount = Object.values(autoSeats).filter(a => a?.seats?.length > 0).length;
              const parts = [];
              if (clearCount > 0) parts.push(`${clearCount} available`);
              if (autoCount > 0) parts.push(`${autoCount} with auto seats`);
              return `Book ${parts.join(' + ')} Day(s)`;
            })()}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FloorPlan;
